import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  message,
  Form,
  Input,
  Button,
  Checkbox,
} from "antd";
import styled from "styled-components";
import PhoneInput from "react-phone-number-input";
import "react-phone-number-input/style.css";
import "../signup.css";

const BASE_URL = process.env.REACT_APP_BASE_URL;

const BodySignup = styled.body`
  padding: 0px;
  margin: 0px;
  font-family: var(--f-primary);
  overscroll-behavior: none;
`;

const StyledButton = styled(Button)`
  && {
    background-color: ${({ disabled }) => (disabled ? "#a3a0b3" : "#3c2f61")};
    border-color: ${({ disabled }) => (disabled ? "#a3a0b3" : "#3c2f61")};
    color: ${({ disabled }) => (disabled ? "#d1d1d1" : "#fff")};
    &:hover {
      background-color: ${({ disabled }) => (disabled ? "#a3a0b3" : "#5a497a")};
      border-color: ${({ disabled }) => (disabled ? "#a3a0b3" : "#5a497a")};
    }
  }
`;

const StyledPhoneInput = styled(PhoneInput)`
  .PhoneInputInput {
    border: 1px solid #d9d9d9;
    border-radius: 6px;
    padding: 4px 11px;
    font-size: 14px;
    line-height: 1.5714285714285714;
    color: rgba(0, 0, 0, 0.88);
    background-color: #ffffff;
    background-image: none;
    transition: all 0.2s;
    height: 32px;
    width: 100%;
    
    &:hover {
      border-color: #4096ff;
    }
    
    &:focus {
      border-color: #4096ff;
      box-shadow: 0 0 0 2px rgba(5, 145, 255, 0.1);
      outline: 0;
    }
  }
  
  .PhoneInputCountrySelect {
    border: 1px solid #d9d9d9;
    border-radius: 6px 0 0 6px;
    border-right: none;
    background-color: #ffffff;
    
    &:hover {
      border-color: #4096ff;
    }
    
    &:focus {
      border-color: #4096ff;
      box-shadow: 0 0 0 2px rgba(5, 145, 255, 0.1);
      outline: 0;
    }
  }
`;

const AcceptInvitation = () => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const { token, email } = useParams();
  const [loading, setLoading] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState("");
  const [defaultCountry, setDefaultCountry] = useState("KE");

  useEffect(() => {
    form.setFieldsValue({ email });
    
    // Get user's country based on IP for default country code
    fetch("https://ipapi.co/json/")
      .then((res) => res.json())
      .then((data) => {
        const country = data?.country;
        if (country && /^[A-Z]{2}$/.test(country)) {
          setDefaultCountry(country);
        }
      })
      .catch(() => {
        setDefaultCountry("KE");
      });
  }, [form, email]);

  const onFinish = async (values) => {
    setLoading(true);
    try {

      const cleanPhoneNumber = phoneNumber?.replace('+', '') || '';
      const response = await axios.post(
        `${BASE_URL}/invites/accept`,
        {
          email: values.email,
          password: values.password,
          first_name: values.first_name,
          last_name: values.last_name,
          phone_number: cleanPhoneNumber,
          token: token,
        },
        { withCredentials: true }
      );

      if (response.data.status === "success") {
        message.success(
          "Invite accepted successfully. Please check your email to get started on Samawati."
        );
        navigate("/invitesuccess");
        form.resetFields();
        setPhoneNumber("");
      } else {
        message.error("Please use a valid invite link");
        form.resetFields();
        setPhoneNumber("");
      }
    } catch (error) {
      console.error("Error accepting invitation:", error);
      message.error("An error occurred while accepting the invitation");
    } finally {
      setLoading(false);
    }
  };

  return (
    <BodySignup>
      <div>
        <div className="getQuote_getQuoteForm__1DMlx">
          <div className="container">
            <Form form={form} onFinish={onFinish} layout="vertical">
              <div>
                <p style={{ fontSize: "20px" }}>Join Samawati</p>
              </div>

              <Form.Item
                name="first_name"
                rules={[{ required: true, message: "Please enter your first name" }]}
              >
                <Input placeholder="First Name" />
              </Form.Item>

              <Form.Item
                name="last_name"
                rules={[{ required: true, message: "Please enter your last name" }]}
              >
                <Input placeholder="Last Name" />
              </Form.Item>

              <Form.Item
                name="email"
                rules={[
                  {
                    required: true,
                    type: "email",
                    message: "Please enter a valid email",
                  },
                ]}
              >
                <Input placeholder="Email" disabled />
              </Form.Item>

              <Form.Item
                name="password"
                rules={[{ required: true, message: "Please enter your password" }]}
              >
                <Input.Password placeholder="Password" />
              </Form.Item>

              <Form.Item
                label="Phone Number"
                name="phone_number"
                rules={[{ required: true, message: "Please enter your phone number" }]}
              >
                <StyledPhoneInput
                  international
                  defaultCountry={defaultCountry}
                  value={phoneNumber}
                  onChange={(val) => setPhoneNumber(val || "")}
                  enableSearch
                  placeholder="Enter phone number"
                />
              </Form.Item>

              <Form.Item>
                <Checkbox
                  checked={termsAccepted}
                  onChange={(e) => setTermsAccepted(e.target.checked)}
                >
                  I agree to the{" "}
                  <a
                    href="https://samawati.co/terms-of-use"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: "blue" }}
                  >
                    Terms of Use
                  </a>{" "}
                  and{" "}
                  <a
                    href="https://samawati.co/privacy-policy"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: "blue" }}
                  >
                    Privacy Policy
                  </a>
                  .
                </Checkbox>
              </Form.Item>

              <Form.Item>
                <StyledButton
                  className="btn btn-sm mb-0 w-100 signupbtn"
                  type="primary"
                  htmlType="submit"
                  loading={loading}
                  disabled={!termsAccepted}
                >
                  Create Account
                </StyledButton>
              </Form.Item>
            </Form>
          </div>
        </div>
      </div>
    </BodySignup>
  );
};

export default AcceptInvitation;